<!-- @format -->

<template>
	<div class="dnb-field-conflict">
		<p class="dnb-field-conflict__label">{{ label }}</p>

		<div class="dnb-field-conflict__options">
			<label
				:for="savedInputId"
				class="dnb-field-conflict__option"
			>
				<RadioButton
					:inputId="savedInputId"
					:name="radioName"
					value="saved"
					:modelValue="modelValue"
					@update:modelValue="$emit('update:modelValue', $event)"
				/>
				<span class="dnb-field-conflict__option-text">
					<span class="dnb-field-conflict__option-heading">
						{{ $t('§eq_operator.dnb_conflict_keep_saved') }}
					</span>
					<span class="dnb-field-conflict__option-value">
						{{ formattedSaved }}
					</span>
				</span>
			</label>

			<label
				:for="dnbInputId"
				class="dnb-field-conflict__option"
			>
				<RadioButton
					:inputId="dnbInputId"
					:name="radioName"
					value="dnb"
					:modelValue="modelValue"
					@update:modelValue="$emit('update:modelValue', $event)"
				/>
				<span class="dnb-field-conflict__option-text">
					<span class="dnb-field-conflict__option-heading">
						{{ $t('§eq_operator.dnb_conflict_use_dnb') }}
					</span>
					<span class="dnb-field-conflict__option-value">
						{{ formattedDnb }}
					</span>
				</span>
			</label>
		</div>

		<Message
			v-if="field === 'date_of_birth'"
			severity="warn"
			class="dnb-field-conflict__warning"
		>
			{{ $t('§eq_operator.dnb_birth_date_conflict_warning') }}
		</Message>
	</div>
</template>

<script>
import { parseISO } from 'date-fns'
import RadioButton from 'primevue/radiobutton'
import Message from 'primevue/message'

const EM_DASH = '—'

export default {
	name: 'DnbFieldConflictResolver',

	components: {
		RadioButton,
		Message,
	},

	props: {
		field: {
			type: String,
			required: true,
			validator: (v) =>
				['first_name', 'last_name', 'date_of_birth', 'country_id'].includes(v),
		},
		label: {
			type: String,
			required: true,
		},
		savedValue: {
			default: null,
		},
		dnbValue: {
			default: null,
		},
		modelValue: {
			type: String,
			required: true,
			validator: (v) => ['saved', 'dnb'].includes(v),
		},
		countryLabelByCountryId: {
			type: Object,
			default: () => ({}),
		},
	},

	emits: ['update:modelValue'],

	computed: {
		radioName() {
			return `dnb-conflict-${this.field}`
		},

		savedInputId() {
			return `${this.radioName}-saved`
		},

		dnbInputId() {
			return `${this.radioName}-dnb`
		},

		formattedSaved() {
			return this.formatValue(this.savedValue)
		},

		formattedDnb() {
			return this.formatValue(this.dnbValue)
		},
	},

	methods: {
		formatValue(value) {
			if (value === null || value === undefined || value === '') return EM_DASH

			if (this.field === 'date_of_birth') {
				try {
					const date = value instanceof Date ? value : parseISO(String(value))
					return this.displayAsDate(date)
				} catch {
					return EM_DASH
				}
			}

			if (this.field === 'country_id') {
				return this.countryLabelByCountryId[value] || EM_DASH
			}

			return String(value)
		},
	},
}
</script>

<style>
.dnb-field-conflict {
	display: flex;
	flex-direction: row;
	align-items: center;
	flex-wrap: wrap;
	gap: 1.5rem;
	padding: 1.25rem 1rem;
	border-radius: 6px;
}

.dnb-field-conflict {
	background-color: var(--color-red-100);
}

.dnb-field-conflict + .dnb-field-conflict {
	margin-top: 0.75rem;
}

.dnb-field-conflict__label {
	font-weight: 600;
	margin: 0;
	flex: 0 0 auto;
	min-width: 8rem;
}

.dnb-field-conflict__options {
	display: flex;
	gap: 1.5rem;
	flex-wrap: wrap;
	flex: 1 1 auto;
}

.dnb-field-conflict__option {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	cursor: pointer;
}

.dnb-field-conflict__option-text {
	display: flex;
	flex-direction: column;
}

.dnb-field-conflict__option-heading {
	font-size: 1.2rem;
	color: var(--text-dark-muted);
}

.dnb-field-conflict__option-value {
	font-weight: 500;
}

.dnb-field-conflict__warning {
	flex: 1 1 100%;
	margin-top: 0.25rem;
}
</style>
